/**
 * Composable for managing premium modals globally
 * Handles showing presell modal after checkout modal is closed
 */
/**
 * The presell discount is switched off with the monthly plan it advertised.
 * Flip this back on only together with an offer that matches what we sell.
 */
const PRESELL_ENABLED = false

export const usePremiumModals = () => {
  // Global state for presell modal
  const showPresellModal = useState<boolean>('premium-presell-modal', () => false)
  const presellSource = useState<'cv-complete' | 'ai-limit' | 'default'>('premium-presell-source', () => 'default')

  // Track if checkout modal was closed
  const checkoutModalClosed = useState<boolean>('checkout-modal-closed', () => false)

  // Cache for database check
  const wasShownCache = useState<boolean | null>('presell-offer-shown-cache', () => null)

  /**
   * Check if special offer was already shown (from database)
   */
  const wasSpecialOfferShown = async (): Promise<boolean> => {
    // Return cached value if available
    if (wasShownCache.value !== null) {
      return wasShownCache.value
    }

    try {
      const response = await $fetch('/api/user/presell-offer', {
        method: 'GET'
      })

      wasShownCache.value = response.wasShown
      return response.wasShown
    } catch (error) {
      console.error('Error checking presell offer status:', error)
      // If API fails, allow showing the offer (better UX)
      return false
    }
  }

  /**
   * Mark special offer as shown in database
   */
  const markSpecialOfferAsShown = async () => {
    try {
      await $fetch('/api/user/presell-offer', {
        method: 'POST'
      })

      wasShownCache.value = true
    } catch (error) {
      console.error('Error marking presell offer as shown:', error)
      // Still mark as shown in cache to prevent multiple attempts
      wasShownCache.value = true
    }
  }

  /**
   * Handle checkout modal close event
   * Shows presell modal after a short delay if user hasn't seen it yet
   */
  const onCheckoutModalClose = async (source?: 'cv-complete' | 'ai-limit' | 'default') => {
    // Retired: the presell offer sold the monthly subscription at 50 % off, and the
    // monthly plan is no longer available (it undercut the Job Search Pack).
    if (!PRESELL_ENABLED) return

    checkoutModalClosed.value = true

    // Check if special offer was already shown
    const wasShown = await wasSpecialOfferShown()
    if (wasShown) {
      checkoutModalClosed.value = false
      return
    }

    // Set source for presell modal
    if (source) {
      presellSource.value = source
    }

    // Show presell modal after a short delay
    setTimeout(async () => {
      showPresellModal.value = true
      await markSpecialOfferAsShown()
      checkoutModalClosed.value = false
    }, 500)
  }

  /**
   * Close presell modal
   */
  const closePresellModal = () => {
    showPresellModal.value = false
  }

  /**
   * Open presell modal directly with a specific source
   */
  const openPresellModal = async (source: 'cv-complete' | 'ai-limit' | 'default' = 'default') => {
    if (!PRESELL_ENABLED) return

    // Check if special offer was already shown
    const wasShown = await wasSpecialOfferShown()
    if (wasShown) {
      return
    }

    presellSource.value = source
    showPresellModal.value = true
    await markSpecialOfferAsShown()
  }

  return {
    showPresellModal,
    presellSource,
    onCheckoutModalClose,
    closePresellModal,
    openPresellModal,
    wasSpecialOfferShown
  }
}
